import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { setNotificationsState, setNotificationsLoading, setNotificationsUnread, markAllNotificationsRead, prependNotification, markNotificationRead } from '../redux/states';

// Simple module-scoped singleton to avoid multiple SSE connections
let notifSSE = null;

export default function useNotifications(options = {}) {
  const dispatch = useDispatch();
  const { items = [], unreadCount = 0, lastFetched = 0, loading = false } = useSelector(s => s.stateData.notifications || {});
  const tokenRef = useRef(null);
  const limit = options.limit || 50;

  const fetchAll = useCallback(async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    tokenRef.current = token;
    if (!token) return;
    try {
      dispatch(setNotificationsLoading(true));
      const [countResp, listResp] = await Promise.all([
        axios.get('/user/notifications/unread-count', { headers: { 'x-access-token': token } }),
        axios.get(`/user/notifications?limit=${limit}&skip=0`, { headers: { 'x-access-token': token } })
      ]);
      const next = Array.isArray(listResp?.data?.items) ? listResp.data.items : [];
      const unread = countResp?.data?.unreadCount ?? listResp?.data?.unreadCount ?? 0;
      dispatch(setNotificationsState({ items: next, unreadCount: unread, lastFetched: Date.now() }));
    } catch (_) {
      dispatch(setNotificationsState({ items: [], unreadCount: 0, lastFetched: Date.now() }));
    } finally {
      dispatch(setNotificationsLoading(false));
    }
  }, [dispatch, limit]);

  const markAll = useCallback(async () => {
    const token = tokenRef.current || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
    if (!token) return;
    try {
      await axios.post('/user/notifications/mark-all-read', {}, { headers: { 'x-access-token': token } });
      dispatch(markAllNotificationsRead());
    } catch (_) {}
  }, [dispatch]);

  const markOne = useCallback(async (id) => {
    if (!id) return;
    const token = tokenRef.current || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
    if (!token) return;
    try {
      // Use fetch with keepalive so it still fires on navigation
      await fetch(`/user/notifications/${encodeURIComponent(id)}/read`, {
        method: 'PATCH',
        keepalive: true,
        headers: { 'x-access-token': token }
      });
      dispatch(markNotificationRead(id));
    } catch (_) {
      // noop
    }
  }, [dispatch]);

  // Establish SSE once to receive live notification.created events
  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    tokenRef.current = token;
    if (!token) return;
    // Avoid creating multiple streams
    if (!notifSSE) {
      try {
        const url = `/user/progress?token=${encodeURIComponent(token)}`;
        notifSSE = new EventSource(url);
        notifSSE.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data || '{}');
            if (data && data.type === 'notification.created') {
              // Update unread and prepend item
              if (data.unreadCount != null) dispatch(setNotificationsUnread(data.unreadCount));
              if (data.item) dispatch(prependNotification(data.item));
            }
          } catch (_) { /* ignore */ }
        };
        notifSSE.onerror = () => {
          // Let the browser handle reconnection; no-op
        };
      } catch (_) {
        // no-op
      }
    }
    return () => { /* intentionally keep SSE open while app is alive */ };
  }, [dispatch]);

  // Initial fetch if stale or forced
  useEffect(() => {
    const now = Date.now();
    const stale = !lastFetched || (now - lastFetched) > 60_000; // 1 minute cache
    if (stale && !loading) fetchAll();
  }, [fetchAll, lastFetched, loading]);

  const preview = useMemo(() => items.slice(0, 4), [items]);

  return {
    items,
    unreadCount,
    loading,
    preview,
    refresh: fetchAll,
    markAllRead: markAll,
    markOneRead: markOne,
  };
}
